import React, { useState, useEffect } from 'react';
import { 
  Container, Typography, Button, Box, CircularProgress, 
  Snackbar, Alert, Dialog, DialogTitle, DialogContent, 
  DialogActions, TextField, IconButton, Paper, Accordion,
  AccordionSummary, AccordionDetails, Chip, FormControlLabel,
  Checkbox, Link, Switch, Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import StarIcon from '@mui/icons-material/Star';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkIcon from '@mui/icons-material/Link';
import ScheduleIcon from '@mui/icons-material/Schedule';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import { QRCodeSVG } from 'qrcode.react';
import API_URL from '../config/api';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';

const resolveMediaUrl = (url) => {
  if (!url) return '';
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  if (url.startsWith('/')) return `${API_URL}${url}`;
  return `${API_URL}/assets/images/${url}`;
};

const withCacheBust = (url) => `${url}${url.includes('?') ? '&' : '?'}_=${Date.now()}`;

const ImageThumbnail = ({ src, alt, size = 40 }) => {
  const [hasError, setHasError] = useState(false);
  const resolvedSrc = resolveMediaUrl(src);

  useEffect(() => {
    setHasError(false);
  }, [src]);

  if (!resolvedSrc || hasError) {
    return (
      <Box
        sx={{
          width: size,
          height: size,
          borderRadius: 1,
          bgcolor: 'grey.100',
          border: '1px solid',
          borderColor: 'grey.300',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}
        title={src ? 'Image preview failed to load' : 'No image selected'}
      >
        <BrokenImageIcon sx={{ fontSize: Math.max(16, size * 0.55), color: 'text.disabled' }} />
      </Box>
    );
  }

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      onError={() => setHasError(true)}
      style={{ width: size, height: size, objectFit: 'cover', borderRadius: 4, flexShrink: 0 }}
    />
  );
};

// --- Normalization helper ---
function normalizeRestaurantData(restaurant) {
  // Ensure all categories and items have required fields
  const categories = (restaurant.categories || []).map((cat, i) => ({
    categoryId: cat.categoryId || cat.category_id || `cat_${Math.random().toString(36).substr(2, 9)}`,
    categoryName: cat.categoryName || cat.category_name || '',
    color: cat.color || '#1976d2',
    items: (cat.items || []).map((item, j) => ({
      itemId: item.itemId || item.item_id || `item_${Math.random().toString(36).substr(2, 9)}`,
      itemName: item.itemName || item.item_name || '',
      description: item.description || '',
      price: item.price !== undefined && item.price !== null ? String(item.price) : '',
      imageUrl: item.imageUrl || item.image_url || '',
      isBestseller: item.isBestseller || item.is_bestseller || false,
      hasImage: item.hasImage || false
    }))
  }));
  return {
    restaurantId: restaurant.restaurant_id || restaurant.restaurantId || '',
    restaurantName: restaurant.restaurant_name || restaurant.restaurantName || '',
    cuisine: restaurant.cuisine || '',
    logoUrl: restaurant.logo_url || restaurant.logoUrl || '',
    categories
  };
}

const RestaurantForm = ({ open, onClose, restaurant, onSave }) => {
  const [formData, setFormData] = useState({
    restaurantId: '',
    restaurantName: '',
    cuisine: '',
    logoUrl: '',
    categories: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingFiles, setUploadingFiles] = useState({});

  useEffect(() => {
    if (restaurant) {
      setFormData(normalizeRestaurantData(restaurant));
    } else {
      setFormData({
        restaurantId: `rest_${Math.random().toString(36).substr(2, 9)}`,
        restaurantName: '',
        cuisine: '',
        logoUrl: '',
        categories: []
      });
    }
  }, [restaurant]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const addCategory = () => {
    const newCategory = {
      categoryId: `cat_${Math.random().toString(36).substr(2, 9)}`,
      categoryName: '',
      color: '#1976d2',
      items: []
    };
    setFormData(prev => ({
      ...prev,
      categories: [...prev.categories, newCategory]
    }));
  };

  const updateCategory = (categoryIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, index) => 
        index === categoryIndex ? { ...cat, [field]: value } : cat
      )
    }));
  };

  const removeCategory = (categoryIndex) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.filter((_, index) => index !== categoryIndex)
    }));
  };

  const addItem = (categoryIndex) => {
    const newItem = {
      itemId: `item_${Math.random().toString(36).substr(2, 9)}`,
      itemName: '',
      description: '',
      price: '',
      imageUrl: '',
      isBestseller: false,
      hasImage: false
    };
    
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, index) => 
        index === categoryIndex 
          ? { ...cat, items: [...cat.items, newItem] }
          : cat
      )
    }));
  };

  const updateItem = (categoryIndex, itemIndex, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, catIndex) => 
        catIndex === categoryIndex 
          ? {
              ...cat,
              items: cat.items.map((item, itemIdx) => 
                itemIdx === itemIndex ? { ...item, [field]: value } : item
              )
            }
          : cat
      )
    }));
  };

  const removeItem = (categoryIndex, itemIndex) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.map((cat, catIndex) => 
        catIndex === categoryIndex 
          ? { ...cat, items: cat.items.filter((_, itemIdx) => itemIdx !== itemIndex) }
          : cat
      )
    }));
  };

  // File upload function for logo (upload-only endpoint)
  const handleLogoUpload = async (file) => {
    if (!file) return;

    setUploadingFiles(prev => ({ ...prev, logo: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('logo', file);
      uploadFormData.append('restaurantId', formData.restaurantId);

      const response = await fetch(`${API_URL}/api/admin/restaurants/upload`, {
        method: 'POST',
        body: uploadFormData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload logo: ${errorText}`);
      }

      const result = await response.json();

      if (result.logoUrl) {
        setFormData(prev => ({ ...prev, logoUrl: result.logoUrl }));
      } else if (result.uploadedFiles && result.uploadedFiles.length > 0) {
        const fileEntry = result.uploadedFiles.find(f => f.field === 'logo') || result.uploadedFiles[0];
        if (fileEntry) setFormData(prev => ({ ...prev, logoUrl: fileEntry.url }));
      }

    } catch (err) {
      setError(`Logo upload failed: ${err.message}`);
    } finally {
      setUploadingFiles(prev => ({ ...prev, logo: false }));
    }
  };

  // File upload function for menu item images
  const handleItemImageUpload = async (file, categoryIndex, itemIndex) => {
    if (!file) return;

    const uploadKey = `${categoryIndex}-${itemIndex}`;
    setUploadingFiles(prev => ({ ...prev, [uploadKey]: true }));

    try {
      const uploadFormData = new FormData();
      uploadFormData.append('itemImages', file);
      uploadFormData.append('restaurantId', formData.restaurantId);

      const response = await fetch(`${API_URL}/api/admin/restaurants/upload`, {
        method: 'POST',
        body: uploadFormData
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to upload image: ${errorText}`);
      }

      const result = await response.json();

      if (result.uploadedFiles && result.uploadedFiles.length > 0) {
        // find first itemImages field entry
        const fileEntry = result.uploadedFiles.find(f => f.field === 'itemImages') || result.uploadedFiles[0];
        if (fileEntry) updateItem(categoryIndex, itemIndex, 'imageUrl', fileEntry.url);
      }

    } catch (err) {
      setError(`Image upload failed: ${err.message}`);
    } finally {
      setUploadingFiles(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('restaurantId', formData.restaurantId);
      formDataToSend.append('restaurantName', formData.restaurantName);
      formDataToSend.append('cuisine', formData.cuisine);
      formDataToSend.append('logoUrl', formData.logoUrl);
      formDataToSend.append('categories', JSON.stringify(formData.categories));

      const response = await fetch(`${API_URL}/api/admin/restaurants`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) {
        throw new Error('Failed to save restaurant');
      }

      const savedRestaurant = await response.json();
      onSave(savedRestaurant);
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers for items
  const onDragEnd = (result, categoryIndex) => {
    if (!result.destination) return;
    const sourceIdx = result.source.index;
    const destIdx = result.destination.index;
    if (sourceIdx === destIdx) return;
    setFormData(prev => {
      const newCategories = [...prev.categories];
      const items = Array.from(newCategories[categoryIndex].items);
      const [removed] = items.splice(sourceIdx, 1);
      items.splice(destIdx, 0, removed);
      newCategories[categoryIndex] = {
        ...newCategories[categoryIndex],
        items
      };
      return { ...prev, categories: newCategories };
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {restaurant ? 'Edit Restaurant' : 'Add New Restaurant'}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        <Box component="form" noValidate sx={{ mt: 2 }}>
          <TextField
            name="restaurantId"
            label="Restaurant ID"
            value={formData.restaurantId}
            onChange={handleChange}
            fullWidth
            margin="normal"
            disabled
          />
          
          <TextField
            name="restaurantName"
            label="Restaurant Name"
            value={formData.restaurantName}
            onChange={handleChange}
            fullWidth
            margin="normal"
            required
          />
          
          <TextField
            name="cuisine"
            label="Cuisine Type"
            value={formData.cuisine}
            onChange={handleChange}
            fullWidth
            margin="normal"
          />
          
          {/* Logo Upload Section */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Restaurant Logo</Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
              {uploadingFiles.logo ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" color="primary">
                    Uploading logo...
                  </Typography>
                </Box>
              ) : formData.logoUrl ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ImageThumbnail src={formData.logoUrl} alt="Logo preview" size={40} />
                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
                  <Typography variant="body2" color="success.main">Logo uploaded</Typography>
                  <Button
                    variant="text"
                    component="label"
                    color="primary"
                  >
                    Change Logo
                    <input
                      type="file"
                      hidden
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          handleLogoUpload(file);
                        }
                        e.target.value = '';
                      }}
                    />
                  </Button>
                </Box>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  startIcon={<CloudUploadIcon />}
                >
                  Upload Logo
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        handleLogoUpload(file);
                      }
                      e.target.value = '';
                    }}
                  />
                </Button>
              )}
            </Box>
            
            {/* Display current logo URL */}
            {formData.logoUrl && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <LinkIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                  Current Logo URL:
                </Typography>
                <Link 
                  href={resolveMediaUrl(formData.logoUrl)} 
                  target="_blank" 
                  rel="noopener"
                  sx={{ 
                    fontSize: '0.875rem', 
                    wordBreak: 'break-all',
                    display: 'block',
                    color: 'primary.main'
                  }}
                >
                  {formData.logoUrl}
                </Link>
              </Box>
            )}
            
            <TextField
              name="logoUrl"
              label="Or enter Logo URL manually"
              value={formData.logoUrl}
              onChange={handleChange}
              fullWidth
              margin="normal"
              size="small"
              helperText="You can either upload a file above or enter a URL manually"
            />
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Menu Categories
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCategory}
              >
                Add Category
              </Button>
            </Box>

            <DragDropContext onDragEnd={({ destination, source }) => {
              if (!destination) return;
              
              const sourceCategoryIndex = source.droppableId.split('-')[1];
              const destCategoryIndex = destination.droppableId.split('-')[1];
              
              if (sourceCategoryIndex === destCategoryIndex) {
                // Reorder items within the same category
                const newItemsOrder = Array.from(formData.categories[sourceCategoryIndex].items);
                const [movedItem] = newItemsOrder.splice(source.index, 1);
                newItemsOrder.splice(destination.index, 0, movedItem);
                
                setFormData(prev => {
                  const newCategories = Array.from(prev.categories);
                  newCategories[sourceCategoryIndex] = {
                    ...newCategories[sourceCategoryIndex],
                    items: newItemsOrder
                  };
                  return {
                    ...prev,
                    categories: newCategories
                  };
                });
              } else {
                // Move item from one category to another
                const sourceItems = Array.from(formData.categories[sourceCategoryIndex].items);
                const destItems = Array.from(formData.categories[destCategoryIndex].items);
                const [movedItem] = sourceItems.splice(source.index, 1);
                
                destItems.splice(destination.index, 0, movedItem);
                
                setFormData(prev => {
                  const newCategories = Array.from(prev.categories);
                  newCategories[sourceCategoryIndex] = {
                    ...newCategories[sourceCategoryIndex],
                    items: sourceItems
                  };
                  newCategories[destCategoryIndex] = {
                    ...newCategories[destCategoryIndex],
                    items: destItems
                  };
                  return {
                    ...prev,
                    categories: newCategories
                  };
                });
              }
            }}>
              {formData.categories.map((category, categoryIndex) => (
                <Droppable key={`droppable-${categoryIndex}`} droppableId={`droppable-${categoryIndex}`}>
                  {(provided) => (
                    <Accordion key={category.categoryId} sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <RestaurantIcon sx={{ color: category.color }} />
                          <Typography>
                            {category.categoryName || `Category ${categoryIndex + 1}`}
                          </Typography>
                          <Chip label={`${category.items.length} items`} size="small" />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                          <TextField
                            label="Category Name"
                            value={category.categoryName}
                            onChange={(e) => updateCategory(categoryIndex, 'categoryName', e.target.value)}
                            fullWidth
                          />
                          <TextField
                            label="Color"
                            type="color"
                            value={category.color}
                            onChange={(e) => updateCategory(categoryIndex, 'color', e.target.value)}
                            sx={{ width: 100 }}
                          />
                          <IconButton
                            onClick={() => removeCategory(categoryIndex)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="subtitle1">Items</Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<MenuBookIcon />}
                            onClick={() => addItem(categoryIndex)}
                          >
                            Add Item
                          </Button>
                        </Box>

                        {/* DRAGGABLE ITEMS LIST */}
                        <DragDropContext onDragEnd={result => onDragEnd(result, categoryIndex)}>
                          <Droppable droppableId={`droppable-${category.categoryId}`}>
                            {(provided) => (
                              <div ref={provided.innerRef} {...provided.droppableProps}>
                                {category.items.map((item, itemIndex) => (
                                  <Draggable key={item.itemId} draggableId={item.itemId} index={itemIndex}>
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        style={{
                                          ...provided.draggableProps.style,
                                          marginBottom: 16,
                                          boxShadow: snapshot.isDragging ? '0 4px 16px rgba(0,0,0,0.12)' : undefined
                                        }}
                                      >
                                        <Paper sx={{ p: 2 }}>
                                          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                            <TextField
                                              label="Item Name"
                                              value={item.itemName}
                                              onChange={(e) => updateItem(categoryIndex, itemIndex, 'itemName', e.target.value)}
                                              fullWidth
                                            />
                                            <TextField
                                              label="Price"
                                              value={item.price}
                                              onChange={(e) => updateItem(categoryIndex, itemIndex, 'price', e.target.value)}
                                              sx={{ width: 120 }}
                                            />
                                            <IconButton
                                              onClick={() => removeItem(categoryIndex, itemIndex)}
                                              color="error"
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Box>
                                          <TextField
                                            label="Description"
                                            value={item.description}
                                            onChange={(e) => updateItem(categoryIndex, itemIndex, 'description', e.target.value)}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            sx={{ mb: 2 }}
                                          />

                                          {/* Item Image Upload Section */}
                                          <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" sx={{ mb: 1 }}>Item Image</Typography>
                                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 1 }}>
                                              {uploadingFiles[`${categoryIndex}-${itemIndex}`] ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <CircularProgress size={20} />
                                                  <Typography variant="body2" color="primary">
                                                    Uploading image...
                                                  </Typography>
                                                </Box>
                                              ) : item.imageUrl ? (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                  <ImageThumbnail src={item.imageUrl} alt="Item preview" size={32} />
                                                  <CheckCircleIcon sx={{ color: 'success.main', fontSize: 16 }} />
                                                  <Typography variant="caption" color="success.main">Image uploaded</Typography>
                                                  <Button
                                                    variant="text"
                                                    component="label"
                                                    size="small"
                                                    color="primary"
                                                  >
                                                    Change Image
                                                    <input
                                                      type="file"
                                                      hidden
                                                      accept="image/*"
                                                      onChange={(e) => {
                                                        const file = e.target.files[0];
                                                        if (file) {
                                                          handleItemImageUpload(file, categoryIndex, itemIndex);
                                                        }
                                                        e.target.value = '';
                                                      }}
                                                    />
                                                  </Button>
                                                </Box>
                                              ) : (
                                                <Button
                                                  variant="outlined"
                                                  component="label"
                                                  size="small"
                                                  startIcon={<CloudUploadIcon />}
                                                >
                                                  Upload Image
                                                  <input
                                                    type="file"
                                                    hidden
                                                    accept="image/*"
                                                    onChange={(e) => {
                                                      const file = e.target.files[0];
                                                      if (file) {
                                                        handleItemImageUpload(file, categoryIndex, itemIndex);
                                                      }
                                                      e.target.value = '';
                                                    }}
                                                  />
                                                </Button>
                                              )}
                                            </Box>
                                            
                                            {/* Display current image URL */}
                                            {item.imageUrl && (
                                              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                                                  <LinkIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                                                  Current Image URL:
                                                </Typography>
                                                <Link 
                                                  href={resolveMediaUrl(item.imageUrl)} 
                                                  target="_blank" 
                                                  rel="noopener"
                                                  sx={{ 
                                                    fontSize: '0.75rem', 
                                                    wordBreak: 'break-all',
                                                    display: 'block',
                                                    color: 'primary.main'
                                                  }}
                                                >
                                                  {item.imageUrl}
                                                </Link>
                                              </Box>
                                            )}
                                            
                                            <TextField
                                              label="Or enter Image URL manually"
                                              value={item.imageUrl}
                                              onChange={(e) => updateItem(categoryIndex, itemIndex, 'imageUrl', e.target.value)}
                                              fullWidth
                                              size="small"
                                              helperText="You can either upload a file above or enter a URL manually"
                                            />
                                          </Box>

                                          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                            <FormControlLabel
                                              control={
                                                <Checkbox
                                                  checked={item.isBestseller}
                                                  onChange={(e) => updateItem(categoryIndex, itemIndex, 'isBestseller', e.target.checked)}
                                                />
                                              }
                                              label={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                  <StarIcon sx={{ color: 'gold', fontSize: 20 }} />
                                                  <Typography variant="body2">Bestseller</Typography>
                                                </Box>
                                              }
                                            />
                                          </Box>
                                        </Paper>
                                      </div>
                                    )}
                                  </Draggable>
                                ))}
                                {provided.placeholder}
                              </div>
                            )}
                          </Droppable>
                        </DragDropContext>
                        {/* END DRAGGABLE ITEMS LIST */}
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Droppable>
              ))}
            </DragDropContext>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AdminDashboard = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', severity: 'success' });
  const [qrModalOpen, setQrModalOpen] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [updatingStatus, setUpdatingStatus] = useState({}); // Track which restaurants are being updated

  const fetchRestaurants = async () => {
    try {
      setLoading(true);
      const response = await fetch(withCacheBust(`${API_URL}/api/admin/restaurants`), {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch restaurants');
      }
      const data = await response.json();
      setRestaurants(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRestaurants();
  }, []);

  const handleAddRestaurant = () => {
    setEditingRestaurant(null);
    setShowForm(true);
  };

  const handleEditRestaurant = async (restaurant) => {
    try {
      // Fetch full restaurant details including categories and items
      const response = await fetch(withCacheBust(`${API_URL}/api/restaurants/${restaurant.restaurant_id}`), {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      if (!response.ok) {
        throw new Error('Failed to fetch restaurant details');
      }
      const fullRestaurant = await response.json();
      setEditingRestaurant(fullRestaurant);
      setShowForm(true);
    } catch (err) {
      setNotification({
        show: true,
        message: err.message,
        severity: 'error'
      });
    }
  };

  const handleToggleRestaurantStatus = async (restaurantId, currentStatus) => {
    setUpdatingStatus(prev => ({ ...prev, [restaurantId]: true }));
    try {
      const response = await fetch(`${API_URL}/api/admin/restaurants/${restaurantId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          isActive: !currentStatus
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update restaurant status');
      }

      await response.json();

      // Update only the specific restaurant in state
      setRestaurants(prev => 
        prev.map(r => 
          r.restaurant_id === restaurantId 
            ? { ...r, is_active: !currentStatus }
            : r
        )
      );

      setNotification({
        show: true,
        message: `Restaurant ${!currentStatus ? 'enabled' : 'disabled'} successfully`,
        severity: 'success'
      });
    } catch (err) {
      setNotification({
        show: true,
        message: err.message,
        severity: 'error'
      });
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [restaurantId]: false }));
    }
  };

  const handleScheduleDisable = async (restaurantId, disableDate) => {
    setUpdatingStatus(prev => ({ ...prev, [restaurantId]: true }));
    try {
      let dateToSend = null;
      
      if (disableDate) {
        // Create date at 11:59 PM India Time (IST = UTC+5:30)
        const selectedDate = new Date(disableDate + 'T23:59:00+05:30');
        dateToSend = selectedDate.toISOString();
      }

      const response = await fetch(`${API_URL}/api/admin/restaurants/${restaurantId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          disableDate: dateToSend
        })
      });

      if (!response.ok) {
        throw new Error('Failed to schedule disable date');
      }

      await response.json();

      // Update only the specific restaurant in state
      setRestaurants(prev => 
        prev.map(r => 
          r.restaurant_id === restaurantId 
            ? { ...r, disable_date: dateToSend }
            : r
        )
      );

      setNotification({
        show: true,
        message: disableDate ? 'Disable date scheduled for 11:59 PM India Time' : 'Disable date cleared',
        severity: 'success'
      });
    } catch (err) {
      setNotification({
        show: true,
        message: err.message,
        severity: 'error'
      });
    } finally {
      setUpdatingStatus(prev => ({ ...prev, [restaurantId]: false }));
    }
  };

  const handleDeleteRestaurant = async (restaurantId) => {
    if (!window.confirm('Are you sure you want to delete this restaurant?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/admin/restaurants/${restaurantId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Failed to delete restaurant');
      }

      setNotification({
        show: true,
        message: 'Restaurant deleted successfully',
        severity: 'success'
      });
      fetchRestaurants();
    } catch (err) {
      setNotification({
        show: true,
        message: err.message,
        severity: 'error'
      });
    }
  };

  const handleSave = async (savedRestaurant) => {
    setNotification({
      show: true,
      message: `Restaurant ${editingRestaurant ? 'updated' : 'created'} successfully`,
      severity: 'success'
    });
    fetchRestaurants();
  };

  const handleQrPreview = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setQrModalOpen(true);
  };

  const downloadQrCode = () => {
    if (!selectedRestaurant) return;
    
    const svg = document.getElementById('preview-qr-code');
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.href = pngFile;
      downloadLink.download = `${selectedRestaurant.restaurant_id}-qrcode.png`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    };
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Restaurant Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddRestaurant}
        >
          Add Restaurant
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))' }}>
          {restaurants.map(restaurant => (
            <Paper
              key={restaurant.restaurant_id}
              sx={{ p: 3 }}
              elevation={2}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6">
                  {restaurant.restaurant_name}
                </Typography>
                <Box>
                  <IconButton
                    size="small"
                    onClick={() => handleEditRestaurant(restaurant)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteRestaurant(restaurant.restaurant_id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>

              {restaurant.cuisine && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Cuisine: {restaurant.cuisine}
                </Typography>
              )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                ID: {restaurant.restaurant_id}
              </Typography>

              {/* Restaurant Status */}
              <Box sx={{ mb: 2, p: 2, bgcolor: restaurant.is_active === false ? 'error.50' : 'success.50', borderRadius: 1, border: '1px solid', borderColor: restaurant.is_active === false ? 'error.200' : 'success.200', position: 'relative' }}>
                {updatingStatus[restaurant.restaurant_id] && (
                  <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'rgba(255, 255, 255, 0.8)', borderRadius: 1, zIndex: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight="bold">
                    Status
                  </Typography>
                  <Chip 
                    label={restaurant.is_active === false ? 'Disabled' : 'Active'} 
                    size="small"
                    color={restaurant.is_active === false ? 'error' : 'success'}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Switch 
                    checked={restaurant.is_active !== false}
                    onChange={() => handleToggleRestaurantStatus(restaurant.restaurant_id, restaurant.is_active !== false)}
                    size="small"
                    disabled={updatingStatus[restaurant.restaurant_id]}
                  />
                  <Typography variant="caption">
                    {restaurant.is_active === false ? 'Enable' : 'Disable'} QR Menu
                  </Typography>
                </Box>
                {restaurant.disable_date && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <ScheduleIcon fontSize="small" />
                      Scheduled disable: {new Date(restaurant.disable_date).toLocaleString('en-IN', { 
                        timeZone: 'Asia/Kolkata',
                        dateStyle: 'medium',
                        timeStyle: 'short'
                      })}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ mt: 1 }}>
                  <Tooltip title="Set a date when this restaurant should be automatically disabled at 11:59 PM India Time">
                    <TextField
                      label="Schedule Disable Date"
                      type="date"
                      size="small"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={restaurant.disable_date ? new Date(restaurant.disable_date).toISOString().split('T')[0] : ''}
                      onChange={(e) => handleScheduleDisable(restaurant.restaurant_id, e.target.value)}
                      disabled={updatingStatus[restaurant.restaurant_id]}
                      sx={{ mt: 1 }}
                    />
                  </Tooltip>
                </Box>
              </Box>

              {/* Show logo if available */}
              {restaurant.logo_url && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <img 
                    src={resolveMediaUrl(restaurant.logo_url)} 
                    alt={`${restaurant.restaurant_name} logo`}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                    style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Logo uploaded
                  </Typography>
                </Box>
              )}

              <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  startIcon={<QrCodeIcon />}
                  onClick={() => handleQrPreview(restaurant)}
                  size="small"
                >
                  View QR Code
                </Button>
              </Box>
              
              {/* Show menu URL */}
              <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                  Menu URL:
                </Typography>
                <Link 
                  href={`${window.location.origin}/menu/${restaurant.restaurant_id}`}
                  target="_blank" 
                  rel="noopener"
                  sx={{ 
                    fontSize: '0.75rem', 
                    wordBreak: 'break-all',
                    display: 'block',
                    color: 'primary.main'
                  }}
                >
                  {`${window.location.origin}/menu/${restaurant.restaurant_id}`}
                </Link>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      <RestaurantForm
        open={showForm}
        onClose={() => setShowForm(false)}
        restaurant={editingRestaurant}
        onSave={handleSave}
      />

      <Dialog 
        open={qrModalOpen} 
        onClose={() => setQrModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">QR Code - {selectedRestaurant?.restaurant_name}</Typography>
            <IconButton onClick={() => setQrModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            {selectedRestaurant && (
              <>
                <QRCodeSVG
                  id="preview-qr-code"
                  value={`${window.location.origin}/menu/${selectedRestaurant.restaurant_id}`}
                  size={300}
                  level="H"
                  includeMargin
                />
                
                {/* Display the QR Code URL prominently */}
                <Box sx={{ mt: 3, mb: 2, p: 2, bgcolor: 'primary.50', borderRadius: 1, width: '100%' }}>
                  <Typography variant="subtitle2" color="primary.main" sx={{ mb: 1, fontWeight: 'bold' }}>
                    <QrCodeIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                    QR Code URL:
                  </Typography>
                  <Link 
                    href={`${window.location.origin}/menu/${selectedRestaurant.restaurant_id}`}
                    target="_blank" 
                    rel="noopener"
                    sx={{ 
                      fontSize: '0.875rem', 
                      wordBreak: 'break-all',
                      display: 'block',
                      color: 'primary.main',
                      fontWeight: 'medium'
                    }}
                  >
                    {`${window.location.origin}/menu/${selectedRestaurant.restaurant_id}`}
                  </Link>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    This URL will display the restaurant's menu when scanned or visited
                  </Typography>
                </Box>
                
                <Button
                  variant="contained"
                  startIcon={<CloudDownloadIcon />}
                  onClick={downloadQrCode}
                  sx={{ mt: 2 }}
                >
                  Download QR Code
                </Button>
              </>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={notification.show}
        autoHideDuration={6000}
        onClose={() => setNotification(prev => ({ ...prev, show: false }))}
      >
        <Alert
          onClose={() => setNotification(prev => ({ ...prev, show: false }))}
          severity={notification.severity}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminDashboard;
